import { ReactNode, useEffect, useMemo, useState } from 'react';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogProps,
  Typography,
} from '@mui/material';

export type Props = {
  title?: string;
  message?: string | ReactNode;
  onConfirm: () => Promise<void>;
  onCancel?: () => void;
  confirmText?: string;
  cancelText?: string;
  confirmButtonStyle?: 'primary' | 'error' | 'warning' | 'info';
  dialogProps?: Partial<DialogProps>;
};

const ConfirmationDialog = ({
  title = 'Are you sure?',
  message = '',
  onConfirm = () => Promise.resolve(),
  onCancel = () => {},
  confirmText = 'Yes',
  cancelText = 'No',
  confirmButtonStyle = 'primary',
  dialogProps,
}: Props) => {
  const [buttonLoading, setButtonLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setButtonLoading(false);
    setOpen(true);
  }, []);

  const confirmHandler = async () => {
    try {
      await onConfirm();
      setOpen(false);
    } catch (e) {
      setButtonLoading(false);
    }
  };
  const { slotProps: dialogSlotProps, maxWidth: dialogMaxWidth, ...restDialogProps } =
    dialogProps ?? {};

  const mergedSlotProps = useMemo(() => {
    const baseBackdrop = {
      sx: {
        backdropFilter: 'blur(4px)',
      },
    };
    const basePaper = {
      sx: {
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.35)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        borderRadius: '8px',
        textAlign: 'center',
      },
    };

    if (!dialogSlotProps) {
      return { backdrop: baseBackdrop, paper: basePaper };
    }

    return {
      ...dialogSlotProps,
      backdrop: {
        ...baseBackdrop,
        ...dialogSlotProps.backdrop,
        sx: {
          ...baseBackdrop.sx,
          ...dialogSlotProps.backdrop?.sx,
        },
      },
      paper: {
        ...basePaper,
        ...dialogSlotProps.paper,
        sx: {
          ...basePaper.sx,
          ...dialogSlotProps.paper?.sx,
        },
      },
    };
  }, [dialogSlotProps]);

  return (
    <Dialog
      {...restDialogProps}
      open={open}
      onClose={(e, reason) => {
        if (['escapeKeyDown', 'backdropClick'].includes(reason)) {
          return;
        }
        onCancel();
      }}
      maxWidth={dialogMaxWidth ?? 'xs'}
      slotProps={mergedSlotProps}
    >
      <DialogTitle sx={{ color: 'rgba(255,255,255,0.92)', fontSize: '0.95rem' }}>
        {title || 'Are you sure?'}
      </DialogTitle>
      {!!message && (
        <DialogContent sx={{ pt: 0 }}>
          {typeof message === 'string' ? (
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem' }}>
              {message}
            </Typography>
          ) : (
            message
          )}
        </DialogContent>
      )}
      <DialogActions sx={{ justifyContent: 'center', gap: 1.5, pb: 2, px: 2 }}>
        <Button
          onClick={onCancel}
          autoFocus
          disabled={buttonLoading}
          variant="contained"
          sx={{
            width: 120,
            height: 32,
            minWidth: '120px',
            maxWidth: '180px',
            gap: '4px',
            opacity: 0.95,
            borderRadius: '100px',
            pt: '9px',
            pr: '16px',
            pb: '9px',
            pl: '16px',
            color: 'rgba(255,255,255,0.9)',
            backgroundColor: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.18)',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.18)',
              boxShadow: 'none',
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          loading={buttonLoading}
          onClick={() => {
            setButtonLoading(true);
            confirmHandler();
          }}
          color={confirmButtonStyle}
          variant="contained"
          sx={{
            width: 120,
            height: 32,
            minWidth: '120px',
            maxWidth: '180px',
            gap: '4px',
            opacity: 0.95,
            borderRadius: '100px',
            pt: '9px',
            pr: '16px',
            pb: '9px',
            pl: '16px',
            ...(confirmButtonStyle === 'error' && { backgroundColor: '#9A221A' }),
            ...(confirmButtonStyle === 'primary' && { backgroundColor: '#0932EC' }),
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
